import React, { useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Users, Clock, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const PatientCard = ({ patient }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: patient.id,
  });
  
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };
  
  // Calculate wait time
  const waitTime = patient.arrivalTime 
    ? Math.floor((Date.now() - new Date(patient.arrivalTime).getTime()) / (1000 * 60))
    : 0;
  
  const waitHours = Math.floor(waitTime / 60);
  const waitMinutes = waitTime % 60;
  
  // ESI color mapping
  const esiColor = {
    1: 'bg-red-600',
    2: 'bg-orange-600',
    3: 'bg-yellow-600',
    4: 'bg-green-600',
    5: 'bg-blue-600'
  };
  
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`
        bg-white border rounded-lg p-3 cursor-grab shadow-sm
        hover:shadow-md transition-all
        ${isDragging ? 'cursor-grabbing z-50 shadow-lg' : ''}
        ${patient.esiLevel <= 2 ? 'border-red-300 bg-red-50' : 'border-gray-200'}
      `}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <div className="font-medium text-sm text-gray-900">{patient.name}</div>
          <div className="text-xs text-gray-600 mt-1">{patient.chiefComplaint}</div>
        </div>
        <div 
          className={`${esiColor[patient.esiLevel]} text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center`}
        >
          {patient.esiLevel}
        </div>
      </div>
      
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center">
          <Clock className="h-3 w-3 mr-1" />
          {waitHours > 0 && `${waitHours}h `}{waitMinutes}m
        </div>
        {patient.age && patient.gender && (
          <div>{patient.age}{patient.gender}</div>
        )}
      </div>
      
      {patient.esiLevel <= 2 && waitTime > 10 && (
        <div className="mt-2 flex items-center text-xs text-red-600">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Exceeds target time
        </div>
      )}
    </div>
  );
};

const PatientQueue = ({ patients }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [filterESI, setFilterESI] = useState('all');
  
  // Filter patients by ESI level
  const filteredPatients = filterESI === 'all' 
    ? patients 
    : patients.filter(p => p.esiLevel === parseInt(filterESI));
  
  // Sort patients by ESI level (critical first)
  const sortedPatients = [...filteredPatients].sort((a, b) => a.esiLevel - b.esiLevel);
  
  // Count by ESI level
  const esiCounts = {
    1: patients.filter(p => p.esiLevel === 1).length,
    2: patients.filter(p => p.esiLevel === 2).length,
    3: patients.filter(p => p.esiLevel === 3).length,
    4: patients.filter(p => p.esiLevel === 4).length,
    5: patients.filter(p => p.esiLevel === 5).length,
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-4">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center">
            <Users className="h-5 w-5 text-gray-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Awaiting Bed ({patients.length})
            </h3>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-400 hover:text-gray-600"
          >
            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </div>
        
        {/* ESI Filter */}
        {isExpanded && (
          <div className="flex space-x-1">
            <button
              onClick={() => setFilterESI('all')}
              className={`px-2 py-1 text-xs rounded ${
                filterESI === 'all' 
                  ? 'bg-gray-800 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All ({patients.length})
            </button>
            {[1, 2, 3, 4, 5].map(level => (
              <button
                key={level}
                onClick={() => setFilterESI(level.toString())}
                className={`px-2 py-1 text-xs rounded ${
                  filterESI === level.toString()
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={{
                  backgroundColor: filterESI === level.toString() 
                    ? level === 1 ? '#dc2626' :
                      level === 2 ? '#ea580c' :
                      level === 3 ? '#ca8a04' :
                      level === 4 ? '#16a34a' : '#2563eb'
                    : undefined
                }}
              >
                ESI-{level} ({esiCounts[level]})
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Patient List */}
      {isExpanded && (
        <div className="p-4 max-h-[600px] overflow-y-auto">
          {sortedPatients.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Users className="h-8 w-8 mx-auto mb-2" />
              <p className="text-sm">No patients waiting for beds</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedPatients.map(patient => (
                <PatientCard key={patient.id} patient={patient} />
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Critical Patient Alert */}
      {esiCounts[1] > 0 && (
        <div className="p-3 bg-red-50 border-t border-red-200">
          <div className="flex items-center text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="font-medium">{esiCounts[1]} critical patient{esiCounts[1] > 1 ? 's' : ''} awaiting bed</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientQueue; 